import React, { useState, useEffect } from 'react';
import {
  Card, Input, Button, Select, Tag, Space, Tabs, List, Typography,
  Modal, Form, message, Row, Col, Divider, Empty, Tooltip,
  Alert,
} from 'antd';
import {
  CalculatorOutlined, PlusOutlined, SearchOutlined, ThunderboltOutlined,
  CheckCircleOutlined, WarningOutlined,
} from '@ant-design/icons';
import { pricingApi } from '../services/api';
import { fmtCN } from '../utils/time';

const { TextArea } = Input;
const { Title, Text, Paragraph } = Typography;

const resultTypeOptions = [
  { value: 'restriction', label: '限制', color: 'red' },
  { value: 'fee_warning', label: '费用提示', color: 'orange' },
  { value: 'requirement', label: '要求', color: 'blue' },
  { value: 'recommendation', label: '建议', color: 'green' },
];

const categoryOptions = [
  { value: 'general', label: '通用' },
  { value: 'cargo', label: '货物' },
  { value: 'location', label: '地点' },
  { value: 'equipment', label: '设备' },
  { value: 'access', label: '通行' },
  { value: 'time', label: '时间' },
];

export default function PricingStrategy() {
  const [activeTab, setActiveTab] = useState('analyze');

  // 分析
  const [selectedConditions, setSelectedConditions] = useState([]);
  const [conditionOptions, setConditionOptions] = useState({});
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeResult, setAnalyzeResult] = useState(null);

  // 规则管理
  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm] = Form.useForm();
  const [newConditions, setNewConditions] = useState([]);
  const [conditionInput, setConditionInput] = useState('');

  useEffect(() => {
    fetchConditions();
    fetchRules();
  }, []);

  const fetchConditions = async () => {
    try {
      const data = await pricingApi.getConditions();
      setConditionOptions(data);
    } catch (e) {}
  };

  const fetchRules = async () => {
    setRulesLoading(true);
    try {
      const data = await pricingApi.listRules({ page_size: 100 });
      setRules(data);
    } catch (e) {} finally {
      setRulesLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (selectedConditions.length === 0) {
      message.warning('请至少选择一个条件');
      return;
    }
    setAnalyzing(true);
    setAnalyzeResult(null);
    try {
      const data = await pricingApi.analyze({ conditions: selectedConditions });
      setAnalyzeResult(data);
      if (data.matched_rules.length === 0) {
        message.info('未找到匹配规则，建议人工确认或新增规则');
      }
    } catch (e) {} finally {
      setAnalyzing(false);
    }
  };

  const handleSaveRule = async (values) => {
    try {
      const payload = { ...values, conditions: newConditions };
      if (editingRule) {
        await pricingApi.updateRule(editingRule.id, payload);
        message.success('规则已修改，已提交后台重新审核');
      } else {
        await pricingApi.createRule(payload);
        message.success('已提交后台审核，待管理员通过后才会进入报价分析匹配池生效');
      }
      setRuleModalOpen(false);
      setEditingRule(null);
      setNewConditions([]);
      ruleForm.resetFields();
      fetchRules();
      fetchConditions();
    } catch (e) {}
  };

  const handleDeleteRule = null;

  const openEditRule = (rule) => {
    setEditingRule(rule);
    setNewConditions(rule.conditions || []);
    ruleForm.setFieldsValue(rule);
    setRuleModalOpen(true);
  };

  const addCondition = () => {
    if (conditionInput.trim() && !newConditions.includes(conditionInput.trim())) {
      setNewConditions([...newConditions, conditionInput.trim()]);
      setConditionInput('');
    }
  };

  // 扁平化所有条件选项
  const allConditionOptions = Object.entries(conditionOptions).flatMap(([cat, conds]) =>
    conds.map(c => ({ value: c, label: `${c}`, group: categoryOptions.find(x => x.value === cat)?.label || cat }))
  );

  const analyzeTab = (
    <div>
      <Card className="card-shadow" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <ThunderboltOutlined style={{ fontSize: 20, color: '#3E6AE1' }} />
          <Text strong style={{ fontSize: 16, fontWeight: 500 }}>报价策略分析</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>多选订单条件，系统自动匹配规则并给出策略建议</Text>
        </div>

        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ marginBottom: 8, display: 'block' }}>选择订单条件（可多选）：</Text>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="选择或输入条件，如：超长货物、私人地址、目的地带围栏等"
            value={selectedConditions}
            onChange={setSelectedConditions}
            options={allConditionOptions}
            showSearch
            tokenSeparators={[',']}
            optionFilterProp="label"
          />
        </div>

        {/* 按分类展示快捷条件 */}
        <div style={{ marginBottom: 16 }}>
          {Object.entries(conditionOptions).map(([cat, conds]) => (
            <div key={cat} style={{ marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: 12, marginRight: 8 }}>
                {categoryOptions.find(c => c.value === cat)?.label || cat}：
              </Text>
              {conds.map(c => (
                <Tag.CheckableTag
                  key={c}
                  checked={selectedConditions.includes(c)}
                  onChange={(checked) => {
                    if (checked) setSelectedConditions([...selectedConditions, c]);
                    else setSelectedConditions(selectedConditions.filter(x => x !== c));
                  }}
                  style={{ margin: '2px', padding: '2px 8px' }}
                >
                  {c}
                </Tag.CheckableTag>
              ))}
            </div>
          ))}
        </div>

        <Button type="primary" size="large" icon={<CalculatorOutlined />} loading={analyzing} onClick={handleAnalyze}>
          分析策略
        </Button>
      </Card>

      {analyzeResult && (
        <Card className="card-shadow" title="分析结果">
          <div style={{ marginBottom: 16 }}>
            <Text strong>输入条件：</Text>
            <Space wrap style={{ marginLeft: 8 }}>
              {analyzeResult.input_conditions.map((c, i) => <Tag key={i} color="blue">{c}</Tag>)}
            </Space>
          </div>

          {analyzeResult.warnings.length > 0 && (
            <Alert
              message="费用风险提示"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {analyzeResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              }
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <div style={{ background: '#F4F4F4', padding: 16, borderRadius: 12, marginBottom: 16 }}>
            <Text strong style={{ color: '#171A20', fontWeight: 500, display: 'block', marginBottom: 8 }}>
              <CheckCircleOutlined style={{ color: '#3E6AE1' }} /> 综合策略建议
            </Text>
            <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
              {analyzeResult.combined_strategy}
            </Paragraph>
          </div>

          <Text strong style={{ marginBottom: 8, display: 'block' }}>匹配的规则 ({analyzeResult.matched_rules.length})：</Text>
          {analyzeResult.matched_rules.length === 0 ? (
            <Empty description="未匹配到规则，建议在「规则管理」中新增" />
          ) : (
            analyzeResult.matched_rules.map((rule, i) => (
              <div key={i} style={{ border: '1px solid #e8e8e8', borderRadius: 6, padding: 12, marginBottom: 8 }}>
                <Space>
                  <Tag color={resultTypeOptions.find(t => t.value === rule.result_type)?.color}>
                    {resultTypeOptions.find(t => t.value === rule.result_type)?.label}
                  </Tag>
                  <Text strong>{rule.rule_name}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>匹配度 {(rule.match_ratio * 100).toFixed(0)}%</Text>
                </Space>
                <div style={{ marginTop: 8, fontSize: 13 }}>
                  <Text type="secondary">条件：</Text>
                  {rule.conditions.map((c, j) => <Tag key={j} style={{ margin: '2px' }}>{c}</Tag>)}
                </div>
                <div style={{ marginTop: 4, fontSize: 13 }}>
                  <Text type="secondary">结果：</Text>
                  <Text strong>{rule.result_strategy}</Text>
                </div>
              </div>
            ))
          )}
        </Card>
      )}
    </div>
  );

  const rulesTab = (
    <div>
      <Card className="card-shadow" style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => {
          setEditingRule(null);
          setNewConditions([]);
          ruleForm.resetFields();
          setRuleModalOpen(true);
        }}>
          新增报价规则
        </Button>
        <Paragraph style={{ marginTop: 12, marginBottom: 0, color: '#666' }}>
          规则逻辑：当订单满足条件列表中的条件时，触发对应的结果策略。例如：条件=["超长货物", "超重货物"] → 结果="不可带尾板卸货"。
        </Paragraph>
      </Card>

      <Card className="card-shadow">
        <List
          loading={rulesLoading}
          dataSource={rules}
          locale={{ emptyText: '暂无规则' }}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Button type="link" size="small" onClick={() => openEditRule(item)}>编辑</Button>,
              ]}
            >
              <List.Item.Meta
                title={
                  <Space>
                    <Text strong>{item.rule_name}</Text>
                    <Tag color={resultTypeOptions.find(t => t.value === item.result_type)?.color}>
                      {resultTypeOptions.find(t => t.value === item.result_type)?.label}
                    </Tag>
                    <Tag>优先级 {item.priority}</Tag>
                    {!item.is_active && <Tag color="red">已停用</Tag>}
                    {/* 审核状态 Tag：pending 审核中；rejected 已拒绝；approved 不显示（默认已生效） */}
                    {item.review_status === 'pending_approval' && (
                      <Tag color="orange">⏳ 审核中（提交后台待通过）</Tag>
                    )}
                    {item.review_status === 'rejected' && (
                      <Tooltip title={item.reject_reason || '被管理员拒绝，请修改后重新提交'}>
                        <Tag color="red">❌ 审核未通过</Tag>
                      </Tooltip>
                    )}
                  </Space>
                }
                description={
                  <div>
                    <div style={{ marginBottom: 4 }}>
                      <Text type="secondary">条件：</Text>
                      {(item.conditions || []).map((c, i) => <Tag key={i} style={{ margin: '2px' }}>{c}</Tag>)}
                    </div>
                    <div>
                      <Text type="secondary">策略结果：</Text>
                      <Text strong style={{ color: '#cf1322' }}>{item.result_strategy}</Text>
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>使用 {item.use_count} 次</Text>
                    {item.review_status === 'approved' && (
                      <Text type="secondary" style={{ fontSize: 12, marginLeft: 12 }}>
                        策略录入人：{item.created_by_name
                          ? `${item.created_by_account_type === 'dispatcher' ? '调度' : '客服/销售'} · ${item.created_by_name}`
                          : (item.source === 'email_ai' ? 'AI邮件归纳' : '系统')} · {item.created_at ? fmtCN(item.created_at) : ''}
                      </Text>
                    )}
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </Card>

      <Modal
        title={editingRule ? '编辑报价规则' : '新增报价规则'}
        open={ruleModalOpen}
        onCancel={() => { setRuleModalOpen(false); setEditingRule(null); }}
        width={600}
        footer={null}
      >
        <Form form={ruleForm} layout="vertical" onFinish={handleSaveRule}>
          <Form.Item name="rule_name" label="规则名称" rules={[{ required: true, message: '请输入规则名称' }]}>
            <Input placeholder="如：超长超重货物限制" />
          </Form.Item>

          <Form.Item label="触发条件（多个条件同时满足时触发）" required>
            <Space.Compact style={{ width: '100%', marginBottom: 8 }}>
              <Input
                placeholder="输入条件，如：超长货物"
                value={conditionInput}
                onChange={(e) => setConditionInput(e.target.value)}
                onPressEnter={addCondition}
              />
              <Button type="primary" onClick={addCondition}>添加</Button>
            </Space.Compact>
            <div>
              {newConditions.map((c, i) => (
                <Tag
                  key={i}
                  closable
                  onClose={() => setNewConditions(newConditions.filter((_, idx) => idx !== i))}
                  style={{ margin: '2px', padding: '4px 8px' }}
                >
                  {c}
                </Tag>
              ))}
              {newConditions.length === 0 && <Text type="secondary">请添加至少一个条件</Text>}
            </div>
          </Form.Item>

          <Form.Item name="result_strategy" label="结果策略" rules={[{ required: true, message: '请输入结果策略' }]}>
            <TextArea rows={3} placeholder="如：不可带尾板卸货 / 需报价时提示可能导致限制性费用" />
          </Form.Item>

          <Form.Item name="result_type" label="结果类型" initialValue="restriction">
            <Select options={resultTypeOptions} />
          </Form.Item>

          <Form.Item name="priority" label="优先级（数字越大越优先）" initialValue={0}>
            <Select
              options={[
                { value: 0, label: '普通' },
                { value: 5, label: '较高' },
                { value: 10, label: '高' },
              ]}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" disabled={newConditions.length === 0}>保存</Button>
              <Button onClick={() => { setRuleModalOpen(false); setEditingRule(null); }}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <h2>报价策略</h2>
        <p className="desc">基于订单条件的规则引擎，多选条件后自动分析报价限制和费用风险</p>
      </div>

      <Card className="card-shadow" styles={{ body: { padding: 0 } }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            { key: 'analyze', label: <span><ThunderboltOutlined /> 策略分析</span>, children: <div style={{ padding: 16 }}>{analyzeTab}</div> },
            { key: 'rules', label: <span><CalculatorOutlined /> 规则管理 ({rules.length})</span>, children: <div style={{ padding: 16 }}>{rulesTab}</div> },
          ]}
        />
      </Card>
    </div>
  );
}
